import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";

import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";

export type Option = {
  id: number;
  name: string;
};

/**
 * Base fields required by the form.
 * Extra fields (like id) are allowed.
 */
type BaseForm = {
  branch: string | number;
  role: string | number;
  firstname: string;
  lastname: string;
  username: string;
  email: string;
  mobile: string;
};

type Props<T extends BaseForm> = {
  form: T;
  setForm: React.Dispatch<React.SetStateAction<T>>;

  branches: Option[];
  roles: Option[];

  branchSearch: string;
  setBranchSearch: (v: string) => void;
  branchesLoading?: boolean;

  loading?: boolean;
  isEdit?: boolean;
  onSubmit: () => void;
};

export function UserForm<T extends BaseForm>({
  form,
  setForm,
  branches,
  roles,
  branchSearch,
  setBranchSearch,
  branchesLoading,
  loading,
  isEdit = false,
  onSubmit,
}: Props<T>) {
  const [branchOpen, setBranchOpen] = useState(false);

  const selectedBranchName =
    branches?.find((b) => String(b.id) === String(form.branch))?.name;

  useEffect(() => {
    if (!branchOpen) setBranchSearch("");
  }, [branchOpen, setBranchSearch]);

    const { t } = useTranslation();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

        {/* BRANCH */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground"> {t("common.branch")}</label>

          <Popover open={branchOpen} onOpenChange={setBranchOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-full justify-between rounded-xl">
                {form.branch
                  ? selectedBranchName || "Selected Branch"
                  : "Select Branch"}
                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search branch..."
                  value={branchSearch}
                  onValueChange={setBranchSearch}
                />

                <CommandEmpty>
                  {branchesLoading ? "Loading..." : "No branch found."}
                </CommandEmpty>

                <CommandGroup>
                  {(branches ?? []).map((branch) => (
                    <CommandItem
                      key={branch.id}
                      value={branch.name}
                      onSelect={() => {
                        setForm((prev) => ({
                          ...prev,
                          branch: String(branch.id),
                        }));
                        setBranchOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          String(form.branch) === String(branch.id)
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      />
                      {branch.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* ROLE */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("common.role")}</label>
          <select
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm w-full"
            value={String(form.role ?? "")}
            onChange={(e) =>
              setForm((s) => ({ ...s, role: e.target.value }))
            }
          >
            <option value="">— Select Role —</option>
            {(roles ?? []).map((r) => (
              <option key={r.id} value={String(r.id)}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        {/* FIRST NAME */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">First Name</label>
          <Input
            className="rounded-xl"
            value={form.firstname}
            onChange={(e) =>
              setForm((s) => ({ ...s, firstname: e.target.value }))
            }
          />
        </div>

        {/* LAST NAME */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Surname</label>
          <Input
            className="rounded-xl"
            value={form.lastname}
            onChange={(e) =>
              setForm((s) => ({ ...s, lastname: e.target.value }))
            }
          />
        </div>

        {/* USERNAME */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Username</label>
          <Input
            className={`rounded-xl ${isEdit ? "opacity-70 cursor-not-allowed" : ""}`}
            value={form.username}
            disabled={isEdit}
            onChange={(e) =>
              setForm((s) => ({ ...s, username: e.target.value }))
            }
          />
        </div>

        {/* EMAIL */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">E-mail</label>
          <Input
            className="rounded-xl"
            value={form.email}
            onChange={(e) =>
              setForm((s) => ({ ...s, email: e.target.value }))
            }
          />
        </div>

        {/* PHONE */}
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs text-muted-foreground">Phone Number</label>
          <Input
            className="rounded-xl"
            value={form.mobile}
            onChange={(e) =>
              setForm((s) => ({ ...s, mobile: e.target.value }))
            }
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={!!loading}
        >
         {loading
  ? t("common.saving")
  : isEdit
  ? t("common.update")
  : t("common.create")}
        </Button>
      </div>
    </form>
  );
}